/**
 * Context: FeatureFlagContext
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R11, CONFIG-V1-R08, BROWSE-V1-R01, BROWSE-V1-R18
 *
 * Provides runtime feature flag state to the entire application.
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules via configuration feature flags.
 * CONFIG-V1-R08: Runtime feature flags to enable or disable platform capabilities.
 * BROWSE-V1-R01: Surfaces the deployment-time browse_enabled setting as
 *                flags.browse so the Browse nav entry (R02) and
 *                /browse route (R03) gate on the same value the
 *                backend uses to gate its routers.
 * BROWSE-V1-R18: Surfaces the deployment-time browse_links_enabled
 *                setting as flags.browseLinks so the result-card
 *                components (R20) gate cross-module Browse links on
 *                the same value. Coherence with flags.browse enforced
 *                at backend startup by R19.
 *
 * Risk Links: R11
 * FMEA Links: FM11
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { FeatureFlags } from '@/types';
import api from '@/lib/api';

/**
 * State shape for the feature flag context
 */
interface FeatureFlagState {
  /** Feature flags from backend configuration */
  flags: FeatureFlags;
  /** Whether flags are currently being loaded */
  isLoading: boolean;
  /** Error message if flag fetch failed */
  error: string | null;
  /** Re-fetch feature flags from backend */
  refetch: () => Promise<void>;
}

/**
 * Default feature flags - all primary modules enabled, optional modules disabled.
 * Used as fallback during loading or on error.
 */
const DEFAULT_FLAGS: FeatureFlags = {
  chat: true,
  semanticSearch: true,
  simpleSearch: true,
  galenSearch: true,
  speech: false,
  speechStreaming: false,
  translation: false,
  admin: true,
  // BROWSE-V1-R01: default false matches the backend Pydantic Setting
  // default; the flag is opt-in per deployment.
  browse: false,
  // BROWSE-V1-R18: default false matches the backend Pydantic Setting
  // default. R19 enforces coherence with browse at backend startup.
  browseLinks: false,
  samlSso: false,
};

/**
 * API response format from /api/config/features endpoint
 */
interface FeatureFlagsApiResponse {
  enable_chat: boolean;
  enable_semantic_search: boolean;
  enable_simple_search: boolean;
  enable_galen_search: boolean;
  enable_speech: boolean;
  enable_speech_streaming: boolean;
  enable_translation: boolean;
  enable_admin: boolean;
  enable_browse: boolean;
  enable_browse_links: boolean;
  enable_saml_sso: boolean;
}

/**
 * Transform backend feature flags format to frontend format.
 * Backend uses snake_case with enable_ prefix, frontend uses camelCase.
 */
function transformApiResponse(response: FeatureFlagsApiResponse): FeatureFlags {
  return {
    chat: response.enable_chat,
    semanticSearch: response.enable_semantic_search,
    simpleSearch: response.enable_simple_search,
    galenSearch: response.enable_galen_search,
    speech: response.enable_speech,
    speechStreaming: response.enable_speech_streaming,
    translation: response.enable_translation,
    admin: response.enable_admin,
    // BROWSE-V1-R01: backend `enable_browse` mirrors the
    // browse_enabled deployment-time setting.
    browse: response.enable_browse,
    // BROWSE-V1-R18: backend `enable_browse_links` mirrors the
    // browse_links_enabled deployment-time setting; R19 enforces
    // coherence with browse_enabled at backend startup.
    browseLinks: response.enable_browse_links,
    samlSso: response.enable_saml_sso,
  };
}

/**
 * Feature flag context - provides access to feature flags throughout the app
 */
const FeatureFlagContext = createContext<FeatureFlagState | undefined>(undefined);

/**
 * Props for FeatureFlagProvider component
 */
interface FeatureFlagProviderProps {
  children: React.ReactNode;
}

/**
 * FeatureFlagProvider component
 *
 * Fetches feature flags from the backend API and provides them to child components.
 * Handles loading and error states gracefully.
 *
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules via configuration feature flags.
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps): React.ReactElement {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch feature flags from backend API
   */
  const fetchFlags = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<FeatureFlagsApiResponse>('/config/features');
      const transformedFlags = transformApiResponse(response.data);
      setFlags(transformedFlags);
    } catch {
      // Authenticated endpoint failed — try the public endpoint for login-relevant flags
      try {
        const pubResponse = await api.get<{ enable_saml_sso: boolean }>('/config/public');
        setFlags((prev) => ({ ...prev, samlSso: pubResponse?.data?.enable_saml_sso ?? prev.samlSso }));
      } catch (pubErr) {
        const errorMessage = pubErr instanceof Error ? pubErr.message : 'Failed to load feature flags';
        setError(errorMessage);
        console.error('[FeatureFlagProvider] Failed to fetch feature flags:', pubErr);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch flags on mount
  useEffect(() => {
    void fetchFlags();
  }, [fetchFlags]);

  const value: FeatureFlagState = {
    flags,
    isLoading,
    error,
    refetch: fetchFlags,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Hook to access feature flags from context
 *
 * @returns Feature flag state including flags, loading state, and error
 * @throws Error if used outside FeatureFlagProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { flags, isLoading } = useFeatureFlags();
 *   if (isLoading) return <Spinner />;
 *   if (flags.chat) return <ChatComponent />;
 *   return null;
 * }
 * ```
 */
export function useFeatureFlags(): FeatureFlagState {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

/**
 * Hook to check if a specific feature is enabled
 *
 * @param feature - The feature key to check
 * @returns Whether the feature is enabled
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isChatEnabled = useIsFeatureEnabled('chat');
 *   if (!isChatEnabled) return null;
 *   return <ChatComponent />;
 * }
 * ```
 */
export function useIsFeatureEnabled(feature: keyof FeatureFlags): boolean {
  const { flags } = useFeatureFlags();
  return flags[feature];
}
